/**
 * Organiser-written texts in every language the app speaks.
 *
 * A competition's description, long description and rules, an event's two
 * descriptions and a series' description are stored twice:
 *
 *   descriptionI18n: { hu: "…", en: "…" }   // per language, as the organiser typed it
 *   description:     "…"                    // the MIRROR — the gym's default-language text
 *
 * The mirror is derived, and only `localizedFieldPatch` writes it. It exists so
 * that everything which has no viewer language, or no gym doc at hand, still
 * reads one plain string: the server-side OG meta a link scraper sees, an old
 * PWA bundle still running after a deploy, the public calendar modal (which
 * reads the competition doc only). "If the viewer's language is empty, show the
 * gym's default language" is then one field read, not an extra `getDoc`.
 *
 * The gym's default language is DERIVED from its `country` — there is no
 * language field on the gym doc, by decision. `country` is set only when the
 * gym is created, so the mirror's language does not drift under a live gym.
 */

/** The languages the app speaks. The ONE list — the web i18n imports it. */
export const SUPPORTED_LANGS = ["hu", "en"];

/** Default language of a gym with no country, or whose country's language the app does not speak. */
export const FALLBACK_LANG = "en";

/**
 * ISO 3166-1 alpha-2 → the country's main language. Holds languages the app
 * does not speak yet on purpose: when German is added, an Austrian gym moves to
 * German on its own, with no data change. Multilingual countries map to their
 * most common language; anything missing falls back to `FALLBACK_LANG`.
 */
export const COUNTRY_LANG = Object.freeze({
  HU: "hu",
  GB: "en", IE: "en", US: "en", CA: "en", AU: "en", NZ: "en", MT: "en",
  AT: "de", DE: "de", LI: "de", CH: "de",
  SK: "sk", CZ: "cs", PL: "pl", RO: "ro", HR: "hr", RS: "sr", SI: "sl",
  UA: "uk", BG: "bg", IT: "it", ES: "es", PT: "pt", FR: "fr", NL: "nl",
  DK: "da", SE: "sv", NO: "no", FI: "fi", GR: "el",
});

/** `"description"` → `"descriptionI18n"`. */
export function localizedKey(field) {
  return field + "I18n";
}

/** The gym's default content language, from anything carrying a `country`. */
export function gymDefaultLang(gym) {
  const code = typeof gym?.country === "string" ? gym.country.trim().toUpperCase() : "";
  const lang = COUNTRY_LANG[code];
  return SUPPORTED_LANGS.includes(lang) ? lang : FALLBACK_LANG;
}

const nonEmpty = (v) => typeof v === "string" && v.trim() !== "";

/** Supported keys only, trimmed, empty values dropped. */
function cleanMap(map) {
  const out = {};
  if (!map || typeof map !== "object") return out;
  for (const lang of SUPPORTED_LANGS) {
    if (nonEmpty(map[lang])) out[lang] = map[lang].trim();
  }
  return out;
}

/**
 * The editor's starting state: every supported language present as a string.
 * A doc saved before the maps existed has only the mirror — it was written in
 * the gym's default language, so it lands on that tab.
 *
 * A mirror that disagrees with its map was written past the helpers — by an old
 * app bundle still running after the deploy, which knows only the plain field.
 * That edit is the newest text, so it wins the default-language tab; otherwise
 * the next save from this editor would silently put the older map text back.
 */
export function seedLocalizedMap(doc, field, defaultLang) {
  const stored = doc?.[localizedKey(field)];
  const mirror = typeof doc?.[field] === "string" ? doc[field] : "";
  const seed = {};
  for (const lang of SUPPORTED_LANGS) seed[lang] = "";
  const onDefault = SUPPORTED_LANGS.includes(defaultLang);
  if (stored && typeof stored === "object") {
    for (const lang of SUPPORTED_LANGS) {
      if (typeof stored[lang] === "string") seed[lang] = stored[lang];
    }
    const implied = localizedFieldPatch(field, stored, defaultLang)[field];
    if (onDefault && mirror.trim() !== "" && mirror.trim() !== implied) seed[defaultLang] = mirror;
  } else if (onDefault) {
    seed[defaultLang] = mirror;
  }
  return seed;
}

/**
 * The write for one text field: the cleaned map and its mirror. The mirror is
 * the default-language text; when that is empty, the first non-empty language
 * in `SUPPORTED_LANGS` order — a text only in English still shows to everyone
 * rather than nothing. `empty` is what an all-empty field stores (`rules` has
 * always been `null` when unset, the descriptions `""`).
 */
export function localizedFieldPatch(field, map, defaultLang, { empty = "" } = {}) {
  const clean = cleanMap(map);
  const mirror = clean[defaultLang] ?? SUPPORTED_LANGS.map((l) => clean[l]).find(Boolean) ?? empty;
  return { [localizedKey(field)]: clean, [field]: mirror };
}

/**
 * Merge a writer's input into the stored text. `plain` is the old single-string
 * input and means the DEFAULT language; `map` overrides per language (unknown
 * keys are ignored). Without this, a writer that sets only the plain field
 * would leave a stale map behind — and `pickLocalized` reads the map first, so
 * the old text would win for every viewer of that language.
 */
export function mergeLocalizedInput(current, field, { plain, map } = {}, defaultLang, opts) {
  const next = seedLocalizedMap(current, field, defaultLang);
  if (plain !== undefined && SUPPORTED_LANGS.includes(defaultLang)) next[defaultLang] = plain ?? "";
  if (map && typeof map === "object") {
    for (const lang of SUPPORTED_LANGS) {
      if (map[lang] !== undefined) next[lang] = map[lang] ?? "";
    }
  }
  return localizedFieldPatch(field, next, defaultLang, opts);
}

/**
 * A new doc's text from a builder's input. A `…I18n` map, when given, IS the
 * text — the plain field next to it is then just its mirror (every editor sends
 * both, from `localizedFieldPatch`) and must not be written into the default
 * language: for an English-only text in a Hungarian gym the mirror is the
 * English text, and taking it as "the default language" would invent a
 * Hungarian translation. Without a map the plain field is the default-language
 * text. A caller holding BOTH a plain value and a partial map as separate
 * intents (an MCP tool's arguments) merges them first with `mergeLocalizedInput`.
 */
export function localizedFromInput(input, field, defaultLang, opts) {
  const map = input?.[localizedKey(field)];
  if (map && typeof map === "object") return localizedFieldPatch(field, map, defaultLang, opts);
  return mergeLocalizedInput({}, field, { plain: input?.[field] ?? undefined }, defaultLang, opts);
}

/**
 * Whether the viewer is reading ANOTHER language's text for this field (the
 * `lang_fallback` analytics param): something is shown, and it is not theirs.
 * A doc without a map has only the mirror, which is the default language — the
 * viewer's own exactly when that is their language.
 */
export function isLocalizedFallback(doc, field, lang, defaultLang) {
  if (!nonEmpty(pickLocalized(doc, field, lang))) return false;
  const stored = doc?.[localizedKey(field)];
  const own = stored && typeof stored === "object"
    ? stored[lang]
    : (lang === defaultLang ? doc?.[field] : "");
  return !nonEmpty(own);
}

/**
 * The text a viewer reads: their language when it has text, else the mirror
 * (the gym's default language), else "". Each field falls back on its own —
 * a translated short description next to an untranslated long one is fine.
 */
export function pickLocalized(doc, field, lang) {
  const own = doc?.[localizedKey(field)]?.[lang];
  if (nonEmpty(own)) return own;
  const mirror = doc?.[field];
  return typeof mirror === "string" ? mirror : "";
}
